import { useTranslation } from 'react-i18next';
import { Coffee } from 'lucide-react';
import RoomCard from '../components/RoomCard';
import Navigation from '../components/Navigation';
import { images } from '../data/images';

interface RoomType {
  id: string;
  type: string;
  capacity: number;
  size: number;
  beds: string;
  view: string[];
  amenities: string[];
  bathroom: string[];
  features: string[];
  accessibility: string[];
  rules: string[];
  image: string;
  gallery: string[];
}

export default function RoomsPage() {
  const { t } = useTranslation();

  const commonAmenities = [
    t('rooms.features.wifi'),
    t('rooms.features.parking'),
    t('rooms.features.aircon'),
    t('rooms.features.pets')
  ];

  const commonBathroom = [
    t('rooms.bathroom.private'),
    t('rooms.bathroom.toiletries'),
    t('rooms.bathroom.bathrobe'),
    t('rooms.bathroom.bidet'),
    t('rooms.bathroom.toilet'),
    t('rooms.bathroom.shower'),
    t('rooms.bathroom.hairdryer')
  ];

  const commonFeatures = [
    t('rooms.features.hardwood'),
    t('rooms.features.desk'),
    t('rooms.features.tv'),
    t('rooms.features.phone'),
    t('rooms.features.satellite'),
    t('rooms.features.fan')
  ];

  const commonRules = [
    t('rooms.rules.no_smoking'),
    t('rooms.rules.pets'),
    t('rooms.rules.no_parties')
  ];

  const rooms: RoomType[] = [
    {
      id: 'double',
      type: t('rooms.types.double'),
      capacity: 2,
      size: 14,
      beds: t('rooms.beds.large_double'),
      view: [
        t('rooms.view.garden'),
        t('rooms.view.city'),
        t('rooms.view.courtyard')
      ],
      amenities: [
        ...commonAmenities,
        t('rooms.amenities.tv'),
        t('rooms.amenities.desk')
      ],
      bathroom: commonBathroom,
      features: commonFeatures,
      accessibility: [
        t('rooms.accessibility.wheelchair'),
        t('rooms.accessibility.ground_floor')
      ],
      rules: commonRules,
      image: images.rooms.standard.doubleGallery[0],
      gallery: images.rooms.standard.doubleGallery
    },
    {
      id: 'triple',
      type: t('rooms.types.triple'),
      capacity: 3,
      size: 18,
      beds: t('rooms.beds.double') + ' + ' + t('rooms.beds.single'),
      view: [
        t('rooms.view.garden'),
        t('rooms.view.city')
      ],
      amenities: [
        ...commonAmenities,
        t('rooms.amenities.tv'),
        t('rooms.amenities.desk'),
        t('rooms.amenities.minibar')
      ],
      bathroom: commonBathroom,
      features: commonFeatures,
      accessibility: [
        t('rooms.accessibility.ground_floor')
      ],
      rules: commonRules,
      image: images.rooms.standard.tripleGallery[0],
      gallery: images.rooms.standard.tripleGallery
    },
    {
      id: 'quadruple',
      type: t('rooms.types.quadruple'),
      capacity: 4,
      size: 22,
      beds: t('rooms.beds.double') + ' + 2 ' + t('rooms.beds.single'),
      view: [
        t('rooms.view.garden')
      ],
      amenities: [
        ...commonAmenities,
        t('rooms.amenities.tv'),
        t('rooms.amenities.desk'),
        t('rooms.amenities.minibar'),
        t('rooms.amenities.safe')
      ],
      bathroom: commonBathroom,
      features: commonFeatures,
      accessibility: [
        t('rooms.accessibility.ground_floor')
      ],
      rules: commonRules,
      image: images.rooms.standard.quadGallery[0],
      gallery: images.rooms.standard.quadGallery
    },
    {
      id: 'family',
      type: t('rooms.types.family'),
      capacity: 5,
      size: 25,
      beds: t('rooms.beds.double') + ' + 3 ' + t('rooms.beds.single'),
      view: [
        t('rooms.view.garden'),
        t('rooms.view.city')
      ],
      amenities: [
        ...commonAmenities,
        t('rooms.amenities.tv'),
        t('rooms.amenities.desk'),
        t('rooms.amenities.minibar'),
        t('rooms.amenities.safe')
      ],
      bathroom: commonBathroom,
      features: commonFeatures,
      accessibility: [
        t('rooms.accessibility.ground_floor')
      ],
      rules: commonRules,
      image: images.rooms.standard.familyGallery[0],
      gallery: images.rooms.standard.familyGallery
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-bronze-50 via-brown-50 to-bronze-100">
      <Navigation />

      <div className="pt-20"></div>

      <main className="max-w-7xl mx-auto px-4 py-16">
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">{t('rooms.title')}</h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {rooms.map((room, index) => (
              <RoomCard
                key={index}
                room={room}
              />
            ))}
          </div>
        </section>

        <section className="mb-20">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50">
            <div className="absolute top-0 right-0 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl"></div>

            <div className="relative z-10 p-12 md:p-16">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-xl mb-6 transform hover:scale-110 transition-transform">
                    <Coffee className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-4">{t('rooms.sharedKitchen.title')}</h2>
                  <div className="w-24 h-1.5 bg-gradient-to-r from-amber-500 to-orange-600 mx-auto mb-6 rounded-full"></div>
                  <p className="text-xl text-stone-700 font-light">
                    {t('rooms.sharedKitchen.subtitle')}
                  </p>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl border border-amber-100">
                  <p className="text-lg md:text-xl text-stone-700 text-center leading-relaxed">
                    {t('rooms.sharedKitchen.description')}
                  </p>

                  <div className="mt-8 flex justify-center">
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full">
                      <div className="w-2 h-2 bg-amber-600 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-stone-800">{t('rooms.sharedKitchen.available')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
